import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideBrowserGlobalErrorListeners,
  provideEnvironmentInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient } from '@angular/common/http';
import { provideRouter, Router } from '@angular/router';
import { datadogRum } from '@datadog/browser-rum';
import {
  angularPlugin,
  provideDatadogErrorHandler,
  provideDatadogRouter,
} from '@datadog/browser-rum-angular';
import { provideAuth, withAuthInterceptor } from '@presencelearning/auth';
import { provideLogging } from '@presencelearning/logging/angular';
import {
  DEFAULT_COMMAND_PALETTE_PLACEHOLDER,
  DEFAULT_COMMAND_PALETTE_SHORTCUT,
  PL_COMMAND_PALETTE_CONFIG,
} from '@presencelearning/ui/command-palette';
import { providePlIcons } from '@presencelearning/icons';
import { detectBrowserLocale, providePlI18n } from '@presencelearning/i18n';
import { TranslocoService } from '@jsverse/transloco';
import { provideRemoteConfig, withConfigCat } from '@presencelearning/remote-config';
import {
  apiOrigins,
  FutureRoomErrorHandler,
  loggingConfig,
  rumInitConfiguration,
} from './observability';
import { routes } from './app.routes';
import { createPagesSearchSource } from './pages-search-source';
import { environment } from '../environments/environment';
import en from '../assets/i18n/en.json';
import es from '../assets/i18n/es.json';

const AVAILABLE_LANGS = ['en', 'es'];

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes),
    provideDatadogRouter(),
    provideDatadogErrorHandler(),
    provideAnimations(),
    provideHttpClient(withAuthInterceptor()),
    // secureRoutes is deny-by-default. Without this allowlist the appointments
    // call leaves with no Authorization header and api-workplace answers 401.
    // The door origin is included so opening a room sends the same token.
    provideAuth({
      issuerUrl: environment.auth.issuerUrl,
      clientId: environment.auth.clientId,
      secureRoutes: apiOrigins(environment.apps, environment.lessonspaceDoorUrl),
    }),
    providePlIcons(),
    providePlI18n({
      availableLangs: AVAILABLE_LANGS,
      defaultLang: detectBrowserLocale(AVAILABLE_LANGS, 'en'),
      translations: { en, es },
    }),
    provideRemoteConfig(withConfigCat({ sdkKey: environment.configCatKey })),
    ...observabilityProviders(),
    // provideCommandPalette() takes sources as a plain array, resolved before
    // any injector exists - it can't inject Router itself. Providing the
    // token directly via useFactory (mirroring @presencelearning/ai's
    // PRESENCE_AI_CONFIG pattern) runs inside an injection context instead.
    {
      provide: PL_COMMAND_PALETTE_CONFIG,
      useFactory: () => ({
        sources: [createPagesSearchSource(inject(Router), inject(TranslocoService))],
        debounceMs: 300,
        minSearchLength: 2,
        shortcut: DEFAULT_COMMAND_PALETTE_SHORTCUT,
        placeholder: DEFAULT_COMMAND_PALETTE_PLACEHOLDER,
      }),
    },
  ],
};

function observabilityProviders() {
  const origins = apiOrigins(environment.apps, environment.lessonspaceDoorUrl);
  const logging = loggingConfig(environment.env, environment.datadogClientToken);
  const rum = rumInitConfiguration(
    environment.datadogApplicationId,
    environment.datadogClientToken,
    environment.env,
    origins
  );
  if (!logging || !rum) {
    return [];
  }
  return [
    provideLogging(logging),
    // provideLogging registers its own ErrorHandler. Keep RUM's Angular
    // reporting and send the same error through the logger.
    { provide: FutureRoomErrorHandler },
    { provide: ErrorHandler, useExisting: FutureRoomErrorHandler },
    provideEnvironmentInitializer(() => {
      datadogRum.init({
        ...rum,
        plugins: [angularPlugin({ router: true })],
      });
    }),
  ];
}
